import {
  Elements,
  PaymentElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useAuth } from 'components/AuthProvider'
import { usePayments } from 'components/PaymentsProvider'
import { FormEvent, useMemo } from 'react'
import useStyles from './styles'
import { useMutateVerifyStripeTransaction } from 'hooks/useTransfers'
import { PaymentsResponse } from 'api/payments/types'

interface CreditOrDebitCardProps {
  handlePaymentSuccess?: () => void
  setDisabledButton: (disabled: boolean) => void
}

interface CreditOrDebitCheckoutProps extends CreditOrDebitCardProps {
  payment: PaymentsResponse
}

const CreditOrDebitCheckout = ({
  handlePaymentSuccess,
  setDisabledButton,
  payment
}: CreditOrDebitCheckoutProps): JSX.Element => {
  const { classes } = useStyles()

  const stripe = useStripe()
  const elements = useElements()

  const { payment_intent } = payment ?? {}

  const { setToast } = useAuth()

  const { mutateAsync: verifyStripeTransaction } =
    useMutateVerifyStripeTransaction()

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setDisabledButton(true)

    // Any bail-out from here on MUST re-enable the button. It was disabled on
    // the line above, and the stale-balance branch below used to `return`
    // without restoring it — so the customer got an error toast and a dead
    // form, with no way forward but a full page reload. Reported 12 Aug:
    // "the buttons are inactive, so I go through my whole usual process again
    // and then same thing happens."
    if (!stripe || !elements) {
      setDisabledButton(false)
      return
    }

    // The "Your wallet balance has changed" guard that used to sit here has
    // been REMOVED. Restoring Rails behaviour is exactly what removing it does.
    //
    // Under Rails this guard NEVER FIRED — it could not. It read
    // `review_data.wallet_use_amount`, and Rails' CreatePaymentIntent
    // #construct_metadata (app/use_cases/create_payment_intent.rb:52-58) built
    // review_data from three keys only:
    //
    //     { payment_amount:, fee:, payment_type: }
    //
    // There was no wallet_use_amount, so the comparison was
    // `Number(balance) < Number(undefined)` → `x < NaN` → always false. Dead
    // code, for the entire life of the Rails app.
    //
    // The cutover killed it by being helpful. The serverless backend does not
    // return the field either, so api/payments/payments.ts defaults it to '0' —
    // and Number('0') is 0, not NaN. The dead guard came alive as
    // `balance < 0`: a block on every customer whose wallet is in ARREARS.
    // Account and insurance fees debited against an empty wallet put customers
    // there routinely, and they are precisely the people trying to top up by
    // card. Reported 12 Aug, ten days after cutover.
    //
    // (The same missing-field-becomes-a-number trap as the NaN row-deletion bug
    // in Buy: an absent API field is not zero, and defaulting it to zero changes
    // behaviour rather than preserving it.)
    //
    // Nothing is lost. This form is a pure card top-up — the customer's existing
    // wallet balance is not an input to a card charge. The authoritative funds
    // check for a wallet-FUNDED purchase lives server-side in the trading path,
    // which is where it belongs and where it still runs.

    const { error } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required'
    })

    if (error) {
      setToast({
        message: error.message as string,
        open: true,
        type: 'error',
        duration: 3000
      })
      setDisabledButton(false)
    } else {
      // The card is CHARGED past this point. Verification failing must never
      // strand the customer silently — and it can fail two ways: a clean
      // {success:false}, or a THROWN ApiError (the axios interceptor throws on
      // a fail envelope). The throw is not hypothetical: until the backend's
      // intent-id fix, verify failed with "Payment intent not found" on EVERY
      // card payment (the API returned the DB row id where Rails returned the
      // Stripe pi_… id), and the uncaught rejection died here — dead form, no
      // message, money moved, customer retries the whole flow. Catch both.
      let verified = false
      try {
        const result = await verifyStripeTransaction(
          payment_intent?.id as string
        )
        verified = Boolean(result?.success)
      } catch {
        verified = false
      }

      if (verified) {
        if (handlePaymentSuccess) {
          handlePaymentSuccess()
        }
        setDisabledButton(true) // In the case that we don't just want to redirect, we probably want to reset the button back to disabled.
      } else {
        // Charged but unconfirmed. Re-enable and say something, rather than
        // going silent — the webhook settle path will still credit the wallet,
        // so point them at it instead of inviting an immediate second charge.
        setToast({
          message:
            'Your payment went through but we could not confirm it yet. Please check your wallet in a minute before trying again.',
          open: true,
          type: 'error',
          duration: 6000
        })
        setDisabledButton(false)
      }
    }
  }

  return (
    <form
      className={classes.form}
      id='credit-or-debit-form'
      data-testid='credit-or-debit-form'
      onSubmit={handleSubmit}
    >
      <PaymentElement id='credit-or-debit-form-payment-element' />
    </form>
  )
}

export default function CreditOrDebitCard(
  props: CreditOrDebitCardProps
): JSX.Element | null {
  const stripePromise = useMemo(
    () => loadStripe(process.env.REACT_APP_STRIPE_CLIENT_PUBLIC_KEY ?? ''),
    []
  )

  const { payment } = usePayments()

  if (payment === undefined) {
    return null
  }

  return (
    // @ts-ignore -- Elements type compatibility with Stripe Promise
    <Elements
      stripe={stripePromise}
      options={{ clientSecret: payment.payment_intent?.client_secret ?? '' }}
    >
      <CreditOrDebitCheckout {...props} payment={payment} />
    </Elements>
  )
}
